import * as _ from "lodash";
import moment from "moment";
import React from 'react';
import ReactDom from "react-dom/client";
import JsxParser from "@recursive-robot/react-jsx-parser";
import { connect } from "react-redux";
import Style from 'style-it';
import browserHistory from "../../shared/history";
import { getColumnDateTimeFormatter, getColumnNumberFormatter, formatDate, formatNumber, getNavigateToPageHandler } from "../../shared/utilities";
import { RootState } from "../../store";
import { dismissBanner, showBanner, switchBranch } from "../../store/app/actions";
import { showDetailedError, showError, showSuccess, showWarning, showInfo } from "../../store/notifications/actions";
import { applyParameterValueChanges, refreshDatasourceByName, updateParameterValue, goToID, goToXYZ, setDatasourceData, patchDatasourceData, setDefaultParameterValue, fetchDatasourceByName } from "../../store/storyline/actions";
import { TemplateDefinition } from "../../store/storyline/types";
import * as TableCellRenderers from "../components/Table/CellRenderers";
import "./Canvas.scss";
import { CanvasBindingsContext } from "../../shared/providers/CanvasBindingsProvider";
import { StaticPlotContext } from "../../shared/providers/StaticPlotProvider";
import { useSettings } from "../../shared/providers/SettingsProvider";
import * as FileSaver from "file-saver";
import clsx from "clsx";
import * as xlsx from "xlsx";
import * as Papa from "papaparse";
import { default as Color } from "colorjs.io";
import { logger, SeverityLevel } from "../../shared/services/loggingService";
import { NavFiltersParameterName } from "../components/NavFilters";
import { useAuth } from "../../auth/AuthContext";
import { PERMISSIONS } from "../../auth/types";
import { TooltipContainerContext } from "../../shared/providers/TooltipContainerProvider";
import { ErrorContainer } from "../../shared/pages/ErrorContainer";
import * as namedColors from "../../shared/colors";
import { useJsxParserComponents } from "../../shared/providers/JsxParserComponentsProvider";
import { CircularProgress } from "../../shared/components";
import { NotificationBanner } from "./NotificationBanner";
import { useWebSocketService } from "../../shared/providers/WebSocketServiceProvider";
import { useTemplateDiagnostics } from "../../shared/hooks/useTemplateDiagnostics";
import { useProfilingSink } from "../../shared/hooks/useProfilingSink";
import { compare } from "compare-versions";
import { snapdom } from "@zumer/snapdom";


const blacklistedAttrs = [];
const blacklistedTags = [];
// Library handles exposed to template bindings never change, so they're shared at module level...
const staticLibraries = {
    moment,
    FileSaver,
    Object,
    xlsx,
    _,
    browserHistory,
    ReactDom,
    Papa,
    logger,
    Color,
    snapdom,
};
const renderError = ({ error }) => {
    logger.trackException({ exception: error, severityLevel: SeverityLevel.Error });

    return (
        <ErrorContainer
            error={{
                type: "Generic",
                message: "An error occurred while parsing template.",
                details: error.toString()
            }}
        />
    );
};

// Backwards-compatibility shim for underscore-based array indexing of frame data...
function getUnderscoreBasedIndexingProxy(frameData: any) {
    return new Proxy(frameData, {
        get: function (target, field) {
            if (typeof field === "string") {
                // If exact path matches, return the value as-is...
                const value = target?.[field] ?? window?.[field];
                if (value !== undefined) {
                    return value;
                }
                // Else, try to parse an underscore-based index from the path...
                else if (field.indexOf("_") >= 0) {
                    const path = field.substr(0, field.lastIndexOf("_"));
                    const index = field.substr(field.lastIndexOf("_") + 1);

                    return target?.[path]?.[index];
                }
            }

            return target?.[field] ?? window?.[field];
        }
    });
}

interface Props {
    template: TemplateDefinition;
    templateName?: string;
    frameData: any;
    parameterValues: Map<string, any>;
    datasourcesInFlight: Set<string>;
    activeBranch: string;
    canvasState: object;
    staticPlot: boolean;
    [key: string]: any;
}

function _Canvas(props: Props) {
    const { template, templateName, frameData, canvasState, navigateTo, updateParameterValueAction, setDefaultParameterValue, applyParameterValueChangesAction, parameterValues, datasourcesInFlight, activeBranch, staticPlot } = props;

    const components = useJsxParserComponents();
    const settings = useSettings();
    const { userMetadata } = useAuth();
    const webSocketService = useWebSocketService();

    const containerRef = React.useRef<HTMLDivElement>(null);

    const onError = useTemplateDiagnostics(templateName, template?.jsx);
    const { onProfile, enabled: profilingEnabled, reactRenderEnabled } = useProfilingSink();

    // TODO: JSX parser now supports lambdas inside bindings, so these higher-order wrappers can be removed at some point...
    // JSX Parser doesn't support lambdas inside bindings, so we need a higher-order function to close over the slide id and expose a suitable parameterless function that can be bound to...
    const navigateToHandler = React.useCallback((slideId) => {
        return () => navigateTo(slideId);
    }, [navigateTo]);

    // Tag parameter updates made explicitly from template-author scripts (e.g. onClick arrow functions)
    // as "UpdateParameterValueCall" so they are distinguishable from built-in input-control changes in tracing.
    // Input components dispatch updateParameterValue directly and are unaffected by this wrapper.
    const updateParameterValue = React.useCallback(
        (parameterName, newValueOrTransformer, isInitialLoad = false, preventDatasourceRefresh = false, sourceDatasourceId = null) =>
            updateParameterValueAction(parameterName, newValueOrTransformer, isInitialLoad, preventDatasourceRefresh, sourceDatasourceId, "UpdateParameterValueCall"),
        [updateParameterValueAction]);
    const applyParameterValueChanges = React.useCallback(applyParameterValueChangesAction, [applyParameterValueChangesAction]);

    const populatedParameterValues = React.useMemo(
        () => Object.fromEntries(Array.from(parameterValues.entries()).filter(([_key, value]) => value !== undefined)),
        [parameterValues]);
    const navigateToPage = React.useCallback(getNavigateToPageHandler(populatedParameterValues, NavFiltersParameterName, browserHistory), [populatedParameterValues]);

    // eslint-disable-next-line no-eval
    const _eval = React.useCallback((expr) => eval(expr), []);

    if (!frameData) return <CircularProgress className="loading-spinner" />;

    const categorizedBindings = {
        canvasState,
        frameData,
        parameterValues: populatedParameterValues,
        functions: {
            navigateToHandler,
            applyParameterValueChanges,
            getColumnNumberFormatter,
            getColumnDateTimeFormatter,
            updateParameterValue,
            setDefaultParameterValue,
            clsx,
            formatNumber,
            formatDate,
            updateParameterValueAction: updateParameterValue,
            applyParameterValueChangesAction: props.applyParameterValueChanges,
            refreshDatasourceByName: props.refreshDatasourceByName,
            patchDatasourceData: props.patchDatasourceData,
            fetchDatasourceByName: props.fetchDatasourceByName,
            showError: props.showError,
            showSuccess: props.showSuccess,
            showDetailedError: props.showDetailedError,
            showWarning: props.showWarning,
            showInfo: props.showInfo,
            goToID: props.goToID,
            goToXYZ: props.goToXYZ,
            navigateToPage,
            trackEvent: logger.trackEvent.bind(logger),
            trackException: logger.trackException.bind(logger),
            trackTrace: logger.trackTrace.bind(logger),
            trackMetric: logger.trackMetric.bind(logger),
            switchBranch: props.switchBranch,
            showBanner: props.showBanner,
            dismissBanner: props.dismissBanner,
            compareVersions: compare,
        },
        libraries: {
            ...staticLibraries,
            webSocketService,
        },
        metadata: {
            userMetadata,
            settings,
            datasourcesInFlight,
            namedColors,
            appMetadata: {
                activeBranch
            }
        }
    };
    window["canvasBindings"] = categorizedBindings;

    // defineFunction closes over `bindings` (declared below) - it's only invoked from template expressions, long after initialization...
    const defineFunction = (...args) => {
        try {
            return new Function(...args).bind(bindings); // eslint-disable-line no-new-func
        }
        catch (e) {
            props.showDetailedError("defineFunction: Function body parsing failed.", e.toString());
        }
    };

    // The `__proto__:` literal key sets the prototype at creation time (unlike a post-creation `__proto__` assignment, which deopts),
    // while the spreads use define-semantics that don't route property writes through the proxy...
    const bindings = {
        __proto__: getUnderscoreBasedIndexingProxy(frameData),
        ...categorizedBindings.canvasState,
        ...props,
        ...categorizedBindings.frameData,
        ...categorizedBindings.parameterValues,
        ...categorizedBindings.functions,
        ...categorizedBindings.libraries,
        ...categorizedBindings.metadata,
        ...TableCellRenderers,
        defineFunction,
        eval: _eval
    };

    const result = <div className="jsx-parser" ref={containerRef}>
        <StaticPlotContext.Provider value={staticPlot}>
            <TooltipContainerContext.Provider value={containerRef}>
                <CanvasBindingsContext.Provider value={bindings}>
                    <JsxParser
                        bindings={bindings}
                        components={components}
                        jsx={template?.jsx}
                        fileName={templateName}
                        blacklistedAttrs={blacklistedAttrs}
                        blacklistedTags={blacklistedTags}
                        onError={onError}
                        onProfile={profilingEnabled ? onProfile : undefined}
                        profileReactRender={profilingEnabled && reactRenderEnabled}
                        renderError={renderError}
                        renderInWrapper={false}
                        key={`${templateName}:${template?.version ?? 0}`} // Force re-parse when template content is updated (version is bumped on every template push)
                    />
                    { !staticPlot && <NotificationBanner key="notification-banner" /> }
                </CanvasBindingsContext.Provider>
            </TooltipContainerContext.Provider>
        </StaticPlotContext.Provider>
    </div>;

    return template?.css ?
        Style.it(template.css, result) :
        result;
}

const Canvas = connect(
    (state: RootState) => ({
        parameterValues: state.storyline.parameterValues,
        canvasState: state.storyline.canvasState,
        datasourcesInFlight: state.storyline.datasourcesInFlight,
        activeBranch: state.app.activeBranch,
    }),
    {
        updateParameterValueAction: updateParameterValue,
        setDefaultParameterValue,
        applyParameterValueChangesAction: applyParameterValueChanges,
        refreshDatasourceByName,
        showError,
        showSuccess,
        showDetailedError,
        showWarning,
        showInfo,
        goToID,
        goToXYZ,
        setDatasourceData,
        patchDatasourceData,
        fetchDatasourceByName,
        switchBranch,
        showBanner,
        dismissBanner,
    })(_Canvas);

export default React.memo(Canvas);